import { useState, type ReactNode } from "react";
import { useQuery } from "@tanstack/react-query";
import { AlertTriangle, Loader2, RefreshCw } from "lucide-react";
import { Line, LineChart, ResponsiveContainer, YAxis } from "recharts";
import { useTheme } from "../../context/ThemeContext";
import EmailProgramsPanel from "./EmailProgramsPanel";
import { programsRangeDays, useEmailPrograms } from "./useEmailPrograms";
import {
  getEmailMonitoring,
  type EmailCampaignSummary,
  type EmailMetricTotals,
  type EmailMonitoringReport,
  type EmailProgramsReport,
} from "../../services/EmailMonitoringService";

// The digest carries nearly all volume, so its daily series stands in as the
// trend line on the summary tiles (CloudWatch only serves one campaign's
// daily metrics per request).
const TREND_CAMPAIGN = "daily-deals-digest";

const RANGE_OPTIONS = [
  { days: 1, label: "Today" },
  { days: 7, label: "7 days" },
  { days: 30, label: "30 days" },
  { days: 90, label: "90 days" },
] as const;

type RangeDays = (typeof RANGE_OPTIONS)[number]["days"];

// Campaign tags are "<program>" or "<program>-<step>"; the iOS cancel save
// is the bare "cancel-save" tag and belongs with the cancel-save steps.
const PROGRAMS: { prefix: string; label: string; kind: string }[] = [
  { prefix: "daily-deals-digest", label: "Daily deals digest", kind: "Marketing · every day" },
  { prefix: "onboarding-drip", label: "Onboarding drip", kind: "New users" },
  { prefix: "intro-value", label: "Intro value", kind: "New users" },
  { prefix: "checkout-nudge", label: "Checkout nudge", kind: "New users" },
  { prefix: "cancel-save", label: "Cancel save", kind: "About to leave" },
  { prefix: "billing-recovery", label: "Billing recovery", kind: "Failed payment" },
  { prefix: "card-expiring", label: "Card expiring", kind: "Billing" },
  { prefix: "churn-winback", label: "Churn winback", kind: "Win-back" },
  { prefix: "mobile-winback-v2", label: "Mobile winback v2", kind: "Win-back" },
  { prefix: "winback-restore", label: "Winback restore", kind: "Win-back" },
  { prefix: "winback-missed", label: "Winback missed deals", kind: "Win-back" },
  { prefix: "repermission", label: "Re-permission", kind: "Dormant free users" },
  { prefix: "local-roundup", label: "Local deal roundup", kind: "Marketing" },
  { prefix: "savings-recap", label: "Savings recap", kind: "Marketing" },
];

const SOURCE_LABELS: Record<string, string> = {
  "rfc8058-one-click": "One-click unsubscribe",
  "unsubscribe-link": "Unsubscribe link",
  "ses-permanent-bounce": "Hard bounces",
  "ses-terminal-bounce": "Soft bounces",
  "ses-complaint": "Spam complaints",
  bounce: "Bounces",
  complaint: "Complaints",
};

// AWS SES enforcement thresholds: reputation review starts near 5% bounce /
// 0.1% complaint; healthy senders stay under 2% / 0.1%.
const BOUNCE_WARN = 2;
const BOUNCE_DANGER = 5;
const COMPLAINT_WARN = 0.1;
const COMPLAINT_DANGER = 0.5;
// A program needs this many sends before "nobody opened" means anything.
const SILENT_PROGRAM_MIN_SENDS = 10;

type HealthLevel = "ok" | "warn" | "danger";

function bounceLevel(rate: number): HealthLevel {
  return rate >= BOUNCE_DANGER ? "danger" : rate >= BOUNCE_WARN ? "warn" : "ok";
}

function complaintLevel(rate: number): HealthLevel {
  return rate >= COMPLAINT_DANGER ? "danger" : rate >= COMPLAINT_WARN ? "warn" : "ok";
}

function formatNumber(value: number) {
  return new Intl.NumberFormat("en-US").format(value || 0);
}

function formatCompact(value: number) {
  return new Intl.NumberFormat("en-US", { notation: "compact", maximumFractionDigits: 1 }).format(value || 0);
}

function pct(part: number, whole: number) {
  return whole > 0 ? (part / whole) * 100 : 0;
}

function formatPct(value: number) {
  if (value === 0) return "0%";
  if (value < 1) return `${value.toFixed(1)}%`;
  return `${Math.round(value)}%`;
}

function compactError(error: unknown) {
  return error instanceof Error && error.message ? error.message : "Could not load email monitoring";
}

const EMPTY_TOTALS: EmailMetricTotals = { sent: 0, delivered: 0, opened: 0, clicked: 0, bounced: 0, complained: 0 };

function addTotals(a: EmailMetricTotals, b: EmailMetricTotals): EmailMetricTotals {
  return {
    sent: a.sent + b.sent,
    delivered: a.delivered + b.delivered,
    opened: a.opened + b.opened,
    clicked: a.clicked + b.clicked,
    bounced: a.bounced + b.bounced,
    complained: a.complained + b.complained,
  };
}

interface ProgramRow {
  key: string;
  label: string;
  kind: string;
  totals: EmailMetricTotals;
  steps: { label: string; sent: number }[];
}

function programFor(campaign: string) {
  return PROGRAMS.find((p) => campaign === p.prefix || campaign.startsWith(`${p.prefix}-`));
}

function stepLabel(campaign: EmailCampaignSummary) {
  return campaign.label.split(" · ")[1] || campaign.label;
}

function groupPrograms(campaigns: EmailCampaignSummary[]): ProgramRow[] {
  const rows = new Map<string, ProgramRow>();
  for (const campaign of campaigns) {
    const program = programFor(campaign.campaign);
    const key = program?.prefix ?? campaign.campaign;
    const row = rows.get(key) ?? {
      key,
      label: program?.label ?? campaign.label,
      kind: program?.kind ?? "Ad-hoc",
      totals: EMPTY_TOTALS,
      steps: [],
    };
    row.totals = addTotals(row.totals, campaign.totals);
    if (program && campaign.campaign !== program.prefix) {
      row.steps.push({ label: stepLabel(campaign), sent: campaign.totals.sent });
    } else if (program && campaign.campaign === program.prefix && key === "cancel-save") {
      row.steps.push({ label: "iOS", sent: campaign.totals.sent });
    }
    rows.set(key, row);
  }
  return [...rows.values()];
}

interface Callout {
  level: "danger" | "warn" | "watch";
  text: string;
}

function buildCallouts(
  report: EmailMonitoringReport,
  totals: EmailMetricTotals,
  active: ProgramRow[],
  programs: EmailProgramsReport | undefined,
  rangeNoun: string,
  sesHealthy: boolean
): Callout[] {
  const callouts: Callout[] = [];
  if (!sesHealthy) {
    callouts.push({
      level: "danger",
      text: `SES is not healthy (${report.ses.enforcementStatus || "unknown status"}${report.ses.sendingEnabled ? "" : ", sending disabled"}). Nothing else on this page matters until that's fixed.`,
    });
  }
  const bounceRate = pct(totals.bounced, totals.sent);
  const bounce = bounceLevel(bounceRate);
  if (bounce !== "ok") {
    callouts.push({
      level: bounce,
      text: `Bounce rate is ${formatPct(bounceRate)} ${rangeNoun} (SES review starts at ${BOUNCE_DANGER}%). Hold the send cap until it's under ${BOUNCE_WARN}%.`,
    });
  }
  const complaintRate = pct(totals.complained, totals.delivered);
  const complaint = complaintLevel(complaintRate);
  if (complaint !== "ok") {
    callouts.push({
      level: complaint,
      text: `${formatNumber(totals.complained)} spam complaints (${complaintRate.toFixed(2)}%) ${rangeNoun}. SES review starts at ${COMPLAINT_WARN}%.`,
    });
  }
  for (const row of active) {
    const rate = pct(row.totals.bounced, row.totals.sent);
    if (row.totals.bounced > 0 && bounceLevel(rate) !== "ok") {
      callouts.push({
        level: "warn",
        text: `${row.label} bounced ${row.totals.bounced} of ${formatNumber(row.totals.sent)} (${formatPct(rate)}). Check ${row.totals.bounced === 1 ? "that address" : "those addresses"}.`,
      });
    }
  }
  for (const row of active) {
    if (row.totals.sent >= SILENT_PROGRAM_MIN_SENDS && row.totals.opened === 0) {
      callouts.push({
        level: "watch",
        text: `${row.label}: nobody opened any of the ${formatNumber(row.totals.sent)} emails. Worth checking the subject line and send time.`,
      });
    }
  }
  if (programs) {
    const emailed = programs.programs.reduce((sum, g) => sum + g.treatedAccounts, 0);
    const cameBack = programs.returners.filter((r) => !r.holdout).length;
    if (emailed > 0 && cameBack === 0) {
      callouts.push({
        level: "watch",
        text: `Nobody has come back from win-back and lifecycle emails yet: 0 of ${formatNumber(emailed)} people emailed in the last ${programsRangeDays(report.days)} days have paid again.`,
      });
    }
  }
  return callouts;
}

const CALLOUT_STYLE: Record<Callout["level"], { tag: string; chrome: string; tagClass: string }> = {
  danger: {
    tag: "Fix now",
    chrome: "bg-red-50 dark:bg-red-900/20",
    tagClass: "text-red-700 dark:text-red-300",
  },
  warn: {
    tag: "Look at",
    chrome: "bg-amber-50 dark:bg-amber-900/20",
    tagClass: "text-amber-800 dark:text-amber-300",
  },
  watch: {
    tag: "Watch",
    chrome: "bg-violet-50 dark:bg-violet-900/20",
    tagClass: "text-violet-700 dark:text-violet-300",
  },
};

function Verdict({
  callouts,
  totals,
  activeCount,
  rangeNoun,
  quotaUsedPct,
}: {
  callouts: Callout[];
  totals: EmailMetricTotals;
  activeCount: number;
  rangeNoun: string;
  quotaUsedPct: number;
}) {
  const worst = callouts.some((c) => c.level === "danger") ? "danger" : callouts.some((c) => c.level === "warn") ? "warn" : "ok";
  const dot = worst === "danger" ? "bg-red-500" : worst === "warn" ? "bg-amber-500" : "bg-emerald-500";
  const headline =
    worst === "danger"
      ? "Needs attention — sending is at risk."
      : worst === "warn"
        ? "Mostly fine — a couple of things to look at."
        : totals.sent > 0
          ? "All clear — email went out and sending is healthy."
          : `All clear, but nothing has been sent ${rangeNoun} yet.`;
  const deliveredAll = totals.sent > 0 && totals.delivered >= totals.sent;

  return (
    <section aria-label="Summary" className="flex flex-col gap-2.5 rounded-2xl bg-white p-4 shadow-sm ring-1 ring-gray-200 dark:bg-dark-800 dark:ring-dark-700">
      <div className="flex items-center gap-3">
        <span className={`h-2 w-2 shrink-0 rounded-full ${dot}`} />
        <h3 className="text-base font-semibold tracking-tight text-gray-950 dark:text-white">{headline}</h3>
      </div>
      {totals.sent > 0 && (
        <p className="max-w-3xl text-sm leading-relaxed text-gray-700 dark:text-dark-100">
          {formatNumber(totals.sent)} emails sent {rangeNoun} across {activeCount} program{activeCount === 1 ? "" : "s"},{" "}
          {deliveredAll ? <strong className="font-semibold">every one delivered</strong> : `${formatPct(pct(totals.delivered, totals.sent))} delivered`}.{" "}
          {formatPct(pct(totals.opened, totals.delivered))} were opened,{" "}
          {totals.clicked === 0 ? "nobody clicked" : `${formatNumber(totals.clicked)} clicked`}. SES is at{" "}
          {quotaUsedPct < 10 ? quotaUsedPct.toFixed(1) : Math.round(quotaUsedPct)}% of the daily quota.
        </p>
      )}
      {callouts.length > 0 && (
        <ul className="flex flex-col gap-1.5">
          {callouts.map((callout) => {
            const style = CALLOUT_STYLE[callout.level];
            return (
              <li key={callout.text} className={`flex gap-3 rounded-lg px-3 py-2 text-xs leading-relaxed ${style.chrome}`}>
                <span className={`w-12 shrink-0 font-semibold ${style.tagClass}`}>{style.tag}</span>
                <span className="text-gray-700 dark:text-dark-100">{callout.text}</span>
              </li>
            );
          })}
        </ul>
      )}
    </section>
  );
}

function Sparkline({ data, dataKey, color }: { data: EmailMonitoringReport["daily"]; dataKey: "delivered" | "opened" | "clicked" | "bounced"; color: string }) {
  if (data.length < 2) return <div className="h-6" />;
  return (
    <div className="h-6 w-full" aria-hidden="true">
      <ResponsiveContainer width="100%" height="100%">
        <LineChart data={data} margin={{ top: 3, right: 2, bottom: 3, left: 2 }}>
          <YAxis hide domain={[0, "dataMax"]} />
          <Line type="monotone" dataKey={dataKey} stroke={color} strokeWidth={2} dot={false} isAnimationActive={false} />
        </LineChart>
      </ResponsiveContainer>
    </div>
  );
}

function StatTile({
  label,
  value,
  sub,
  foot,
  footClass,
  children,
}: {
  label: string;
  value: string;
  sub: string;
  foot: string;
  footClass?: string;
  children?: ReactNode;
}) {
  return (
    <article className="flex min-w-0 flex-col gap-1 rounded-xl bg-white px-4 py-3 shadow-sm ring-1 ring-gray-200 dark:bg-dark-800 dark:ring-dark-700">
      <p className="text-xs text-gray-500 dark:text-dark-300">{label}</p>
      <p className="flex flex-wrap items-baseline gap-x-2">
        <span className="font-mono text-2xl font-medium tabular-nums text-gray-950 dark:text-white">{value}</span>
        <span className="text-xs text-gray-500 dark:text-dark-300">{sub}</span>
      </p>
      {children}
      <p className={`text-xs ${footClass || "text-gray-500 dark:text-dark-300"}`}>{foot}</p>
    </article>
  );
}

function trendNote(daily: EmailMonitoringReport["daily"], key: "opened" | "clicked") {
  const total = daily.reduce((sum, d) => sum + d[key], 0);
  if (key === "clicked") {
    if (total === 0) return "No clicks on the digest this week";
    const days = daily.filter((d) => d.clicked > 0);
    if (days.length === 1) {
      const day = new Date(`${days[0].date}T00:00:00Z`).toLocaleDateString("en-US", { month: "short", day: "numeric", timeZone: "UTC" });
      return `All ${formatNumber(total)} digest clicks this week came on ${day}`;
    }
    return `${formatNumber(total)} digest clicks this week`;
  }
  const delivered = daily.reduce((sum, d) => sum + d.delivered, 0);
  return `Digest 7-day avg ${formatPct(pct(total, delivered))}`;
}

function ProgramTable({ rows, idle, rangeNoun }: { rows: ProgramRow[]; idle: ProgramRow[]; rangeNoun: string }) {
  return (
    <section aria-labelledby="email-sent-heading" className="flex flex-col gap-2 rounded-2xl bg-white p-4 shadow-sm ring-1 ring-gray-200 dark:bg-dark-800 dark:ring-dark-700">
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <h3 id="email-sent-heading" className="text-sm font-semibold text-gray-950 dark:text-white">
          What went out {rangeNoun}
        </h3>
        <p className="text-xs text-gray-500 dark:text-dark-300">Grouped by program · steps fold into their program</p>
      </div>
      {rows.length === 0 ? (
        <p className="py-4 text-center text-xs text-gray-500 dark:text-dark-300">No marketing email went out {rangeNoun}.</p>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full min-w-[720px] text-xs">
            <thead>
              <tr className="border-b border-gray-200 text-xs text-gray-500 dark:border-dark-700 dark:text-dark-300">
                <th scope="col" className="py-1.5 pr-3 text-left font-medium">Program</th>
                <th scope="col" className="px-3 py-1.5 text-right font-medium">Sent</th>
                <th scope="col" className="px-3 py-1.5 text-right font-medium">Opened</th>
                <th scope="col" className="px-3 py-1.5 text-right font-medium">Clicked</th>
                <th scope="col" className="px-3 py-1.5 text-right font-medium">Bounced</th>
                <th scope="col" className="w-56 py-1.5 pl-3 text-left font-medium">Open rate</th>
              </tr>
            </thead>
            <tbody className="font-mono tabular-nums">
              {rows.map((row) => {
                const openRate = pct(row.totals.opened, row.totals.delivered);
                const bounceRate = pct(row.totals.bounced, row.totals.sent);
                const bounceBad = row.totals.bounced > 0 && bounceLevel(bounceRate) !== "ok";
                const stepsSent = row.steps.filter((s) => s.sent > 0);
                return (
                  <tr key={row.key} className="border-b border-gray-100 last:border-0 dark:border-dark-700/60">
                    <td className="py-2 pr-3 font-sans">
                      <div className="font-semibold text-gray-950 dark:text-white">{row.label}</div>
                      <div className="text-xs text-gray-500 dark:text-dark-300">
                        {stepsSent.length > 0
                          ? stepsSent.map((s) => `${s.label}: ${formatNumber(s.sent)}`).join(" · ")
                          : row.kind}
                      </div>
                    </td>
                    <td className="px-3 py-2 text-right text-gray-950 dark:text-white">{formatNumber(row.totals.sent)}</td>
                    <td className="px-3 py-2 text-right text-gray-700 dark:text-dark-100">{formatNumber(row.totals.opened)}</td>
                    <td className="px-3 py-2 text-right text-gray-700 dark:text-dark-100">{formatNumber(row.totals.clicked)}</td>
                    <td className={`px-3 py-2 text-right ${bounceBad ? "font-semibold text-red-600 dark:text-red-400" : "text-gray-700 dark:text-dark-100"}`}>
                      {formatNumber(row.totals.bounced)}
                      {row.totals.complained > 0 && (
                        <span className="ml-1.5 text-xs font-semibold text-red-600 dark:text-red-400">· {row.totals.complained} spam</span>
                      )}
                    </td>
                    <td className="py-2 pl-3">
                      <div className="flex items-center gap-2.5">
                        <div className="h-1.5 flex-1 rounded-full bg-gray-100 dark:bg-dark-700">
                          <div className="h-1.5 rounded-full bg-violet-600 dark:bg-violet-400" style={{ width: `${Math.min(100, openRate)}%` }} />
                        </div>
                        <span className="w-11 text-right text-gray-700 dark:text-dark-100">{formatPct(openRate)}</span>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
      {idle.length > 0 && (
        <p className="text-xs text-gray-500 dark:text-dark-300">
          Nothing sent {rangeNoun} from {idle.map((row) => row.label).join(", ")}.
        </p>
      )}
    </section>
  );
}

function HealthPanel({ report, sesHealthy, quotaUsedPct, rangeNoun }: { report: EmailMonitoringReport; sesHealthy: boolean; quotaUsedPct: number; rangeNoun: string }) {
  const sources = report.optOuts.bySource.filter((row) => row.source !== "legacy-unattributed");
  const legacy = report.optOuts.current - report.optOuts.withTimestamp;
  const trackingSince = new Date(report.trackingSince).toLocaleDateString("en-US", { month: "short", day: "numeric" });
  const flags = [
    { ok: report.ses.sendingEnabled, label: "Sending enabled" },
    { ok: report.ses.productionAccess, label: "Production access" },
    { ok: report.ses.trackingEnabled, label: "Open/click tracking" },
  ].filter((flag) => !flag.ok);

  return (
    <section aria-labelledby="email-health-heading" className="flex min-w-0 flex-col gap-3 rounded-2xl bg-white p-4 shadow-sm ring-1 ring-gray-200 dark:bg-dark-800 dark:ring-dark-700">
      <div className="flex items-center justify-between gap-2">
        <h3 id="email-health-heading" className="text-sm font-semibold text-gray-950 dark:text-white">Sender &amp; list health</h3>
        <span
          className={`rounded-full px-2.5 py-1 text-xs font-semibold ${
            sesHealthy
              ? "bg-emerald-50 text-emerald-700 dark:bg-emerald-900/20 dark:text-emerald-300"
              : "bg-red-50 text-red-700 dark:bg-red-900/20 dark:text-red-300"
          }`}
        >
          {sesHealthy ? "Healthy" : report.ses.enforcementStatus || "Unknown"}
        </span>
      </div>

      <div className="flex flex-col gap-2">
        <div className="flex justify-between text-xs text-gray-700 dark:text-dark-100">
          <span>SES last 24h</span>
          <span className="font-mono tabular-nums">
            {formatNumber(report.ses.sentLast24Hours)} / {formatCompact(report.ses.max24HourSend)}
          </span>
        </div>
        <div className="h-2 rounded-full bg-gray-100 dark:bg-dark-700">
          <div
            className={`h-2 rounded-full ${quotaUsedPct >= 80 ? "bg-amber-500" : "bg-emerald-500"}`}
            style={{ width: `${Math.min(100, Math.max(1, quotaUsedPct))}%` }}
          />
        </div>
        <p className="text-xs text-gray-500 dark:text-dark-300">
          Includes transactional mail · {formatNumber(report.ses.maxSendRate)}/sec limit
        </p>
        {flags.length > 0 && (
          <p className="text-xs font-semibold text-red-600 dark:text-red-400">Off: {flags.map((f) => f.label).join(", ")}</p>
        )}
      </div>

      <dl className="grid grid-cols-[1fr_auto] gap-x-4 gap-y-1.5 text-xs">
        <dt className="text-gray-700 dark:text-dark-100">Suppressed addresses</dt>
        <dd className="font-mono tabular-nums text-gray-950 dark:text-white">{formatNumber(report.ses.suppression.total)}</dd>
        <dt className="text-gray-700 dark:text-dark-100">Opted out, all time</dt>
        <dd className="font-mono tabular-nums text-gray-950 dark:text-white">
          {formatNumber(report.optOuts.current)}{" "}
          <span className="text-gray-500 dark:text-dark-300">(+{formatNumber(report.optOuts.inRange)} {rangeNoun})</span>
        </dd>
        {legacy > 0 && (
          <>
            <dt className="pl-3.5 text-gray-500 dark:text-dark-300">Before tracking ({trackingSince})</dt>
            <dd className="font-mono tabular-nums text-gray-500 dark:text-dark-300">{formatNumber(legacy)}</dd>
          </>
        )}
        {sources.map((row) => (
          <div key={row.source} className="contents">
            <dt className="pl-3.5 text-gray-500 dark:text-dark-300">{SOURCE_LABELS[row.source] || row.source}</dt>
            <dd className="font-mono tabular-nums text-gray-500 dark:text-dark-300">{formatNumber(row.count)}</dd>
          </div>
        ))}
      </dl>
    </section>
  );
}

export default function EmailMonitoringTab() {
  const [days, setDays] = useState<RangeDays>(1);
  const { isDark } = useTheme();
  const reportQuery = useQuery({
    queryKey: ["email-monitoring", days, TREND_CAMPAIGN],
    queryFn: () => getEmailMonitoring(days, TREND_CAMPAIGN),
    staleTime: 60_000,
    refetchInterval: 5 * 60_000,
  });
  // Sparklines always show at least a week so "Today" still has a trend.
  const trendDays = Math.max(days, 7);
  const trendQuery = useQuery({
    queryKey: ["email-monitoring", trendDays, TREND_CAMPAIGN],
    queryFn: () => getEmailMonitoring(trendDays, TREND_CAMPAIGN),
    staleTime: 60_000,
    refetchInterval: 5 * 60_000,
  });
  const programsQuery = useEmailPrograms(days);

  const report = reportQuery.data;
  const trend = trendQuery.data?.daily ?? [];
  const isToday = days === 1;
  const rangeNoun = isToday ? "today" : `in the last ${days} days`;
  const colors = isDark
    ? { delivered: "#199e70", opened: "#9085e9", clicked: "#d95926", bounced: "#a8a29e" }
    : { delivered: "#12805c", opened: "#5b21b6", clicked: "#c2410c", bounced: "#6b7280" };

  return (
    <div className="mx-auto flex max-w-6xl flex-col gap-3">
      <header className="flex flex-wrap items-end justify-between gap-3">
        <h2 className="text-lg font-semibold tracking-tight text-gray-950 dark:text-white">Email</h2>
        <div className="flex flex-wrap items-center gap-2">
          <div role="group" aria-label="Range" className="flex rounded-lg bg-white p-0.5 ring-1 ring-gray-200 dark:bg-dark-800 dark:ring-dark-700">
            {RANGE_OPTIONS.map((option) => (
              <button
                key={option.days}
                type="button"
                aria-pressed={days === option.days}
                onClick={() => setDays(option.days)}
                className={`rounded-md px-3 py-1 text-xs transition-colors ${
                  days === option.days
                    ? "bg-gray-950 font-semibold text-white dark:bg-white dark:text-gray-950"
                    : "text-gray-500 hover:text-gray-900 dark:text-dark-300 dark:hover:text-white"
                }`}
              >
                {option.label}
              </button>
            ))}
          </div>
          <button
            type="button"
            onClick={() => {
              reportQuery.refetch();
              trendQuery.refetch();
              programsQuery.refetch();
            }}
            className="flex h-8 items-center gap-1.5 rounded-lg bg-white px-3 text-xs text-gray-600 ring-1 ring-gray-200 transition-colors hover:bg-gray-50 dark:bg-dark-800 dark:text-dark-200 dark:ring-dark-700 dark:hover:bg-dark-700"
          >
            <RefreshCw className={`h-3.5 w-3.5 ${reportQuery.isFetching ? "animate-spin" : ""}`} />
            {report ? `Updated ${new Date(report.generatedAt).toLocaleTimeString([], { hour: "numeric", minute: "2-digit" })}` : "Refresh"}
          </button>
        </div>
      </header>

      {reportQuery.isPending ? (
        <div className="flex h-48 items-center justify-center gap-2 rounded-2xl bg-white text-xs text-gray-500 ring-1 ring-gray-200 dark:bg-dark-800 dark:text-dark-300 dark:ring-dark-700">
          <Loader2 className="h-4 w-4 animate-spin text-violet-600" />
          Loading SES and subscriber metrics
        </div>
      ) : reportQuery.error || !report ? (
        <div className="flex items-start gap-2 rounded-xl bg-red-50 px-4 py-2 text-sm font-medium text-red-700 ring-1 ring-red-200 dark:bg-red-900/20 dark:text-red-300 dark:ring-red-800">
          <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
          {compactError(reportQuery.error)}
        </div>
      ) : (
        <EmailReport
          report={report}
          trend={trend}
          programs={programsQuery.data}
          days={days}
          rangeNoun={rangeNoun}
          colors={colors}
        />
      )}
    </div>
  );
}

function EmailReport({
  report,
  trend,
  programs,
  days,
  rangeNoun,
  colors,
}: {
  report: EmailMonitoringReport;
  trend: EmailMonitoringReport["daily"];
  programs: EmailProgramsReport | undefined;
  days: number;
  rangeNoun: string;
  colors: { delivered: string; opened: string; clicked: string; bounced: string };
}) {
  const grouped = groupPrograms(report.campaigns ?? []);
  const active = grouped.filter((row) => row.totals.sent > 0).sort((a, b) => b.totals.sent - a.totals.sent);
  const idle = grouped.filter((row) => row.totals.sent === 0);
  const totals = active.reduce((sum, row) => addTotals(sum, row.totals), EMPTY_TOTALS);
  const sesHealthy = Boolean(report.ses.sendingEnabled && report.ses.productionAccess && report.ses.enforcementStatus === "HEALTHY");
  const quotaUsedPct = report.ses.max24HourSend > 0 ? (report.ses.sentLast24Hours / report.ses.max24HourSend) * 100 : 0;
  const callouts = buildCallouts(report, totals, active, programs, rangeNoun, sesHealthy);
  const bounceRate = pct(totals.bounced, totals.sent);
  const complaintRate = pct(totals.complained, totals.delivered);

  return (
    <>
      <Verdict callouts={callouts} totals={totals} activeCount={active.length} rangeNoun={rangeNoun} quotaUsedPct={quotaUsedPct} />

      <section aria-label={`Numbers ${rangeNoun}`} className="grid grid-cols-2 gap-3 xl:grid-cols-4">
        <StatTile
          label="Delivered"
          value={formatPct(pct(totals.delivered, totals.sent))}
          sub={`${formatNumber(totals.delivered)} of ${formatNumber(totals.sent)}`}
          foot={`Digest, last ${Math.max(days, 7)} days`}
        >
          <Sparkline data={trend} dataKey="delivered" color={colors.delivered} />
        </StatTile>
        <StatTile
          label="Opened"
          value={formatPct(pct(totals.opened, totals.delivered))}
          sub={`${formatNumber(totals.opened)} people`}
          foot={trendNote(trend, "opened")}
        >
          <Sparkline data={trend} dataKey="opened" color={colors.opened} />
        </StatTile>
        <StatTile
          label="Clicked"
          value={formatPct(pct(totals.clicked, totals.delivered))}
          sub={`${formatNumber(totals.clicked)} people`}
          foot={trendNote(trend, "clicked")}
        >
          <Sparkline data={trend} dataKey="clicked" color={colors.clicked} />
        </StatTile>
        <StatTile
          label="Bounced · complaints"
          value={formatNumber(totals.bounced)}
          sub={`bounce${totals.bounced === 1 ? "" : "s"} · ${formatNumber(totals.complained)} complaint${totals.complained === 1 ? "" : "s"}`}
          foot={
            bounceLevel(bounceRate) === "ok" && complaintLevel(complaintRate) === "ok"
              ? `${formatPct(bounceRate)} — well under the ${BOUNCE_DANGER}% SES limit`
              : `${formatPct(bounceRate)} bounce · ${complaintRate.toFixed(2)}% complaints`
          }
          footClass={
            bounceLevel(bounceRate) === "danger" || complaintLevel(complaintRate) === "danger"
              ? "font-semibold text-red-600 dark:text-red-400"
              : bounceLevel(bounceRate) === "warn" || complaintLevel(complaintRate) === "warn"
                ? "font-semibold text-amber-600 dark:text-amber-400"
                : undefined
          }
        >
          <Sparkline data={trend} dataKey="bounced" color={colors.bounced} />
        </StatTile>
      </section>

      <ProgramTable rows={active} idle={idle} rangeNoun={rangeNoun} />

      <div className="grid min-w-0 gap-3 lg:grid-cols-[minmax(0,3fr)_minmax(0,2fr)]">
        <EmailProgramsPanel days={days} />
        <HealthPanel report={report} sesHealthy={sesHealthy} quotaUsedPct={quotaUsedPct} rangeNoun={rangeNoun} />
      </div>

      <p className="px-1 text-xs text-gray-500 dark:text-dark-300">
        Opens may be undercounted — some mail apps block tracking images. Days are UTC.
      </p>
    </>
  );
}
